import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';
import { scrubBreadcrumb, scrubSentryEvent } from './sentryScrub';

const REPLAY_OPTIONS = {
  maskAllText: true,
  blockAllMedia: true,
};

/**
 * Replay is ~430 KB, so it is not part of the main bundle: it is fetched from Sentry's CDN once the page has loaded and
 * then added to the already-initialized client (sample rates stay in the init options). Errors are captured by the
 * client from the start; only the replay attached to events from the first seconds of a session can be missing.
 */
function loadReplayLazily() {
  const load = () => {
    Sentry.lazyLoadIntegration('replayIntegration')
      .then((replayIntegration) => {
        Sentry.addIntegration(replayIntegration(REPLAY_OPTIONS));
      })
      .catch(() => {
        // Blocked by an ad blocker or offline: error tracking keeps working without replay
      });
  };

  if (document.readyState === 'complete') load();
  else window.addEventListener('load', load, { once: true });
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn(
      'Sentry DSN not configured. Error tracking is disabled. Set VITE_SENTRY_DSN to enable.',
    );
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Never let Sentry attach PII (IP addresses, request bodies, etc.) by default.
    sendDefaultPii: false,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Performance Monitoring
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Performance monitoring sample rate
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay sample rates — never record healthy sessions, only ones
    // that hit an error (masked + media-blocked via replayIntegration above).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Deep-scrub every outgoing event so no PII (emails, names, tokens, phone,
    // addresses, ID data, …) leaves the browser. See `sentryScrub.ts`.
    beforeSend(event) {
      return scrubSentryEvent(event);
    },

    // Drop/redact email + token values from breadcrumb trails before they are
    // attached to any event.
    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },

    // Ignore common errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',

      // Network errors
      'Network request failed',
      'NetworkError',
      'Failed to fetch',

      // React specific
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });

  loadReplayLazily();
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; role: string }) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    // Identify users by id + role only — never email or any other PII.
    Sentry.setUser({
      id: user.id,
      role: user.role,
    });
  }
}

/**
 * Clear user context from Sentry (on logout)
 */
export function clearSentryUser() {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(null);
  }
}

/**
 * Add custom breadcrumb for tracking user actions
 */
export function addSentryBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      message,
      category,
      level: 'info',
      data,
    });
  }
}
